import { Button, FormControl, FormLabel, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Stack, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { checkHealthcareInstitutionIdDuplicate, createPharmacy, uploadFile } from "../../../../api";
import { colors } from "../../../../styles";
import { getApiErrorCode } from "../../../../utils";
import FileAttach from "../../../Auth/SignUp/FileAttach";
import { getSignUpErrorMessage } from "../../../Auth/SignUp/errors";
import { FieldMessage, underlineInputProps } from "../../../Auth/SignUp/StepLayout";

interface CreatePharmacyModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface IPharmacyForm {
    name: string;
    address: string;
    addressDetail: string;
    bizRegNo: string;
    healthcareInstitutionId: string;
}

const healthcareInstitutionIdRegex = /^\d{8}$/;
const bizRegNoRegex = /^\d{3}-?\d{2}-?\d{5}$/;

export default function CreatePharmacyModal({ isOpen, onClose }: CreatePharmacyModalProps) {
    const queryClient = useQueryClient();
    const toast = useToast();
    const [businessRegistrationCertImage, setBusinessRegistrationCertImage] = useState<File | null>(null);
    const [establishmentRegistrationCertImage, setEstablishmentRegistrationCertImage] = useState<File | null>(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [debouncedHealthcareInstitutionId, setDebouncedHealthcareInstitutionId] = useState("");

    const { register, handleSubmit, watch, setValue, reset } = useForm<IPharmacyForm>();
    const name = watch("name") ?? "";
    const address = watch("address") ?? "";
    const addressDetail = watch("addressDetail") ?? "";
    const bizRegNo = watch("bizRegNo") ?? "";
    const healthcareInstitutionId = watch("healthcareInstitutionId") ?? "";
    const isBizRegNoFormatValid = bizRegNoRegex.test(bizRegNo);
    const isHealthcareInstitutionIdFormatValid = healthcareInstitutionIdRegex.test(healthcareInstitutionId);

    useEffect(() => {
        if (isOpen) {
            reset({ name: "", address: "", addressDetail: "", bizRegNo: "", healthcareInstitutionId: "" });
            setBusinessRegistrationCertImage(null);
            setEstablishmentRegistrationCertImage(null);
            setErrorMessage("");
        }
    }, [isOpen, reset]);

    useEffect(() => {
        const id = setTimeout(() => setDebouncedHealthcareInstitutionId(healthcareInstitutionId), 400);
        return () => clearTimeout(id);
    }, [healthcareInstitutionId]);

    const {
        isLoading: isCheckingHealthcareInstitutionId,
        isError: isHealthcareInstitutionIdCheckError,
        error: healthcareInstitutionIdCheckError,
    } = useQuery({
        queryKey: ["checkHealthcareInstitutionIdDuplicate", debouncedHealthcareInstitutionId],
        queryFn: () => checkHealthcareInstitutionIdDuplicate({ healthcareInstitutionId: debouncedHealthcareInstitutionId }),
        enabled: isOpen && !!debouncedHealthcareInstitutionId && healthcareInstitutionIdRegex.test(debouncedHealthcareInstitutionId),
        retry: false,
    });

    const isHealthcareInstitutionIdDuplicate =
        isHealthcareInstitutionIdCheckError &&
        getApiErrorCode(healthcareInstitutionIdCheckError) === "HEALTHCARE_INSTITUTION_ALREADY_USED";
    const isHealthcareInstitutionIdCheckUnavailable =
        isHealthcareInstitutionIdCheckError && !isHealthcareInstitutionIdDuplicate;

    const checkHealthcareInstitutionIdDuplicateMutation = useMutation({
        mutationFn: checkHealthcareInstitutionIdDuplicate,
        retry: false,
    });
    const uploadFileMutation = useMutation({ mutationFn: uploadFile });
    const createPharmacyMutation = useMutation({
        mutationFn: createPharmacy,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["me"] });
            toast({
                title: "약국 정보가 등록되었습니다.",
                status: "success",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
            onClose();
        },
    });

    const bizRegNoHint = (() => {
        if (!bizRegNo) return null;
        if (!isBizRegNoFormatValid) {
            return { message: "사업자등록번호는 XXX-XX-XXXXX 형식으로 입력해주세요.", tone: "error" as const };
        }
        return null;
    })();

    const healthcareInstitutionHint = (() => {
        if (!healthcareInstitutionId) return null;
        if (!isHealthcareInstitutionIdFormatValid) {
            return { message: "요양기관 번호는 8자리 숫자로 입력해주세요.", tone: "error" as const };
        }
        if (isCheckingHealthcareInstitutionId) {
            return { message: "중복 확인 중...", tone: "muted" as const };
        }
        if (isHealthcareInstitutionIdDuplicate) {
            return { message: "이미 사용 중인 요양기관 번호입니다.", tone: "error" as const };
        }
        if (isHealthcareInstitutionIdCheckUnavailable) {
            return { message: "요양기관 번호 중복 확인에 실패했습니다.", tone: "error" as const };
        }
        return { message: "사용 가능한 요양기관 번호입니다.", tone: "success" as const };
    })();

    const onSave = async (data: IPharmacyForm) => {
        if (!businessRegistrationCertImage || !establishmentRegistrationCertImage) {
            setErrorMessage("약국 관련 증명 서류를 모두 첨부해주세요.");
            return;
        }

        setErrorMessage("");

        try {
            await checkHealthcareInstitutionIdDuplicateMutation.mutateAsync({
                healthcareInstitutionId: data.healthcareInstitutionId,
            });
            const businessRegistrationCertImageUrl = await uploadFileMutation.mutateAsync({
                file: businessRegistrationCertImage,
            });
            const establishmentRegistrationCertImageUrl = await uploadFileMutation.mutateAsync({
                file: establishmentRegistrationCertImage,
            });
            await createPharmacyMutation.mutateAsync({
                name: `${data.name}약국`,
                address: `${data.address} ${data.addressDetail}`.trim(),
                bizRegNo: data.bizRegNo,
                healthcareInstitutionId: data.healthcareInstitutionId,
                businessRegistrationCertImage: businessRegistrationCertImageUrl,
                establishmentRegistrationCertImage: establishmentRegistrationCertImageUrl,
            });
        } catch (error) {
            setErrorMessage(getSignUpErrorMessage(error, "약국 정보 등록 중 문제가 발생했습니다."));
        }
    };

    const isFormFilled =
        Boolean(name.trim()) &&
        Boolean(address.trim()) &&
        Boolean(addressDetail.trim()) &&
        isBizRegNoFormatValid &&
        isHealthcareInstitutionIdFormatValid &&
        Boolean(businessRegistrationCertImage) &&
        Boolean(establishmentRegistrationCertImage);

    return (
        <Modal motionPreset="slideInBottom" isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text fontSize={20} fontWeight={700} color={colors.black}>약국 정보 등록</Text>
                    <ModalCloseButton />
                </ModalHeader>
                <ModalBody padding={10} as="form" onSubmit={handleSubmit(onSave)}>
                    <FormControl>
                        <FormLabel fontSize="sm" color={colors.blue}>약국 이름</FormLabel>
                        <HStack>
                            <Input
                                {...register("name", { required: true })}
                                type="text"
                                placeholder="약국 이름"
                                {...underlineInputProps}
                            />
                            <Text fontSize={16} fontWeight={700} color={colors.g1} whiteSpace="nowrap">약국</Text>
                        </HStack>
                    </FormControl>
                    <FormControl marginTop={5}>
                        <FormLabel fontSize="sm" color={colors.blue}>주소</FormLabel>
                        <HStack width="100%">
                            <Input
                                {...register("address", { required: true })}
                                type="text"
                                readOnly
                                placeholder="주소 입력"
                                {...underlineInputProps}
                            />
                            <Button
                                type="button"
                                variant="outline"
                                color={colors.g1}
                                size="sm"
                                onClick={() => {
                                    new window.daum.Postcode({
                                        oncomplete: (data: { address: string }) => setValue("address", data.address),
                                    }).open();
                                }}
                            >
                                주소 찾기
                            </Button>
                        </HStack>
                    </FormControl>
                    <FormControl marginTop={5}>
                        <FormLabel fontSize="sm" color={colors.blue}>상세 주소</FormLabel>
                        <Input
                            {...register("addressDetail", { required: true })}
                            type="text"
                            placeholder="상세 주소"
                            {...underlineInputProps}
                        />
                    </FormControl>
                    <FormControl marginTop={5}>
                        <FormLabel fontSize="sm" color={colors.blue}>사업자등록번호</FormLabel>
                        <Input
                            {...register("bizRegNo", { required: true })}
                            type="text"
                            placeholder="000-00-00000"
                            {...underlineInputProps}
                        />
                        <FieldMessage message={bizRegNoHint?.message} tone={bizRegNoHint?.tone} />
                    </FormControl>
                    <FormControl marginTop={5}>
                        <FormLabel fontSize="sm" color={colors.blue}>요양기관 번호</FormLabel>
                        <Input
                            {...register("healthcareInstitutionId", { required: true })}
                            type="text"
                            placeholder="요양기관 번호"
                            {...underlineInputProps}
                        />
                        <FieldMessage message={healthcareInstitutionHint?.message} tone={healthcareInstitutionHint?.tone} />
                    </FormControl>
                    <FormControl marginTop={10}>
                        <FormLabel fontSize="sm" color={colors.blue} marginBottom={5}>증명 서류</FormLabel>
                        <FileAttach
                            label="01 | 사업자 등록증"
                            inputId="create-business-upload"
                            file={businessRegistrationCertImage}
                            onChange={(file) => {
                                setBusinessRegistrationCertImage(file);
                                setErrorMessage("");
                            }}
                        />
                        <FileAttach
                            label="02 | 약국 개설 등록증"
                            inputId="create-establishment-upload"
                            file={establishmentRegistrationCertImage}
                            onChange={(file) => {
                                setEstablishmentRegistrationCertImage(file);
                                setErrorMessage("");
                            }}
                            showDivider
                        />
                    </FormControl>
                    <FieldMessage message={errorMessage} />
                    <Stack marginTop={8} width="100%">
                        <Button
                            type="submit"
                            isLoading={
                                uploadFileMutation.isPending ||
                                createPharmacyMutation.isPending ||
                                checkHealthcareInstitutionIdDuplicateMutation.isPending
                            }
                            isDisabled={
                                !isFormFilled ||
                                isCheckingHealthcareInstitutionId ||
                                isHealthcareInstitutionIdCheckError
                            }
                            width="100%"
                            height="52px"
                            backgroundColor={colors.blue}
                            color={colors.white}
                            borderRadius={10}
                        >
                            저장
                        </Button>
                    </Stack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}
